import React, { useMemo } from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Radius, Typography } from '../../theme';
import { useTheme } from '../../theme/useTheme';
import type { TonyteTheme } from '../../theme/themes';
import { daylightStyle } from '../../theme/daylightStyle';

interface Props {
  variant: 'event' | 'venue' | 'location';
  label: string;
  title: string;
  metaLines: string[];
  teaser?: string;
  imageUrl?: string;
  ctaLabel: string;
  onPress: () => void;
  isMe: boolean;
  // Historical/no-longer-openable event card: muted image, "Expired" badge,
  // no active-looking CTA arrow, tap disabled — the card stays visible as
  // chat history but must not read as a live, tappable event.
  expired?: boolean;
}

/**
 * Premium embedded card for shared events/venues/one-shot locations —
 * frosted glass + metallic outline, image bleeding into the body via a
 * gradient fade (no harsh cutoff).
 */
const SharedEventCard: React.FC<Props> = ({ variant, label, title, metaLines, teaser, imageUrl, ctaLabel, onPress, isMe, expired }) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createAppearanceStyles(theme), [theme]);
  return <TouchableOpacity
    style={[styles.card, isMe ? styles.cardMe : styles.cardThem, isMe ? appearance.cardMe : appearance.cardThem]}
    activeOpacity={expired ? 1 : 0.85}
    onPress={expired ? undefined : onPress}
    disabled={expired}
  >
    {imageUrl ? (
      <View style={styles.imageWrap}>
        <Image source={{ uri: imageUrl }} style={[styles.image, expired && styles.imageMuted]} resizeMode="cover" />
        <LinearGradient
          pointerEvents="none"
          colors={theme.isDark
            ? ['rgba(10,14,22,0)', 'rgba(10,14,22,0.9)']
            : ['rgba(255,250,242,0)', 'rgba(248,239,226,0.94)']}
          style={styles.imageFade}
        />
        {expired ? (
          <View style={styles.expiredBadge}>
            <Icon name="clock" size={10} color="rgba(255,255,255,0.85)" />
            <Text style={styles.expiredBadgeText}>{label}</Text>
          </View>
        ) : null}
      </View>
    ) : (
      <View style={[styles.image, styles.imageFallback, appearance.imageFallback, expired && styles.imageMuted]}>
        <Icon name={variant === 'location' ? 'map-pin' : variant === 'venue' ? 'home' : 'calendar'} size={20} color={theme.isDark ? Colors.textMuted : theme.colors.iconMuted} />
      </View>
    )}
    <View style={styles.body}>
      {!imageUrl || !expired ? <Text style={[styles.label, appearance.label]}>{label}</Text> : null}
      <Text style={[styles.title, appearance.title, expired && styles.titleMuted, expired && appearance.titleMuted]} numberOfLines={2}>{title}</Text>
      {metaLines.filter(Boolean).map((line, index) => (
        <Text key={index} style={[styles.meta, appearance.meta, expired && styles.metaMuted, expired && appearance.metaMuted]} numberOfLines={1}>{line}</Text>
      ))}
      {teaser ? <Text style={[styles.teaser, appearance.teaser]} numberOfLines={2}>{teaser}</Text> : null}
      <View style={[styles.ctaButton, appearance.ctaButton, expired && styles.ctaButtonExpired]}>
        <Text style={[styles.ctaText, appearance.ctaText, expired && styles.ctaTextExpired]}>{ctaLabel}</Text>
        {expired ? null : <Icon name="arrow-right" size={13} color={theme.isDark ? Colors.textPrimary : theme.colors.textPrimary} />}
      </View>
    </View>
  </TouchableOpacity>;
};

export default SharedEventCard;

const styles = StyleSheet.create({
  card: {
    borderRadius: Radius.lg,
    overflow: 'hidden',
    borderWidth: 1,
    width: 220,
  },
  cardMe: {
    borderColor: 'rgba(255,255,255,0.22)',
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  cardThem: {
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(255,255,255,0.055)',
  },
  imageWrap: {
    width: '100%',
    height: 108,
  },
  image: {
    width: '100%',
    height: 108,
    backgroundColor: Colors.bgCardAlt,
  },
  imageFade: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 36,
  },
  imageFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: { padding: 10, gap: 3 },
  label: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: 10,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    fontWeight: Typography.bold,
  },
  title: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  meta: { color: 'rgba(255,255,255,0.8)', fontSize: Typography.xs },
  metaMuted: { color: 'rgba(255,255,255,0.45)' },
  teaser: { color: 'rgba(255,255,255,0.65)', fontSize: Typography.xs },
  ctaButton: {
    marginTop: 7,
    alignSelf: 'flex-start',
    minHeight: 28,
    paddingHorizontal: 10,
    borderRadius: Radius.md,
    backgroundColor: 'rgba(255,255,255,0.14)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  ctaText: { color: Colors.textPrimary, fontSize: Typography.xs, fontWeight: Typography.bold },
  imageMuted: { opacity: 0.4 },
  titleMuted: { color: 'rgba(255,255,255,0.55)' },
  ctaButtonExpired: {
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  ctaTextExpired: { color: 'rgba(255,255,255,0.5)' },
  expiredBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: Radius.md,
    backgroundColor: 'rgba(3,7,15,0.72)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  expiredBadgeText: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 10,
    fontWeight: Typography.bold,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
});

const createAppearanceStyles = (theme: TonyteTheme) => StyleSheet.create({
  cardMe: daylightStyle(theme, {
    backgroundColor: 'rgba(255,239,220,0.96)',
    borderColor: 'rgba(242,125,38,0.28)',
    ...theme.shadows.elevated,
  }),
  cardThem: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  imageFallback: daylightStyle(theme, {
    backgroundColor: theme.colors.surface,
  }),
  label: daylightStyle(theme, { color: theme.colors.accent }),
  title: daylightStyle(theme, { color: theme.colors.textPrimary }),
  meta: daylightStyle(theme, { color: theme.colors.textSecondary }),
  teaser: daylightStyle(theme, { color: theme.colors.textSecondary }),
  titleMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  metaMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  ctaButton: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderWidth: 1,
    borderColor: theme.colors.borderStrong,
  }),
  ctaText: daylightStyle(theme, { color: theme.colors.textPrimary }),
});
